/**
 * Warehouse detail — V1.0.x inventory location detail surface.
 *
 * Layout mirrors invoice-detail exactly:
 *   • Page root is `flex flex-col h-full` — header is a static flex child,
 *     main has `overflow-y: auto`. No `position: sticky` needed.
 *   • Header: back · Warehouse label · name pill · type chip · status pill ·
 *     Default badge · sub-line (items · value · created) · [Edit] right.
 *   • Body: optional description strip → movements table (all unique content).
 *     Cover strip and Details panel removed — they duplicated header data.
 *
 * @see apps/web/src/features/invoices/detail/invoice-detail.css — CSS reference
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowDown, ArrowUp, Edit3, Power, PowerOff, Warehouse } from "lucide-react";
import { ConfirmDialog, toast } from "@flint/ui";
import type { stock as stockContract } from "@flint/api-contract";
import type { z } from "zod";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { avatarClassFor, avatarCssColorFor, initialsFor, ListPagination } from "../../master-list/index.ts";
import "./warehouse-detail-page.css";

type MovementRow = z.infer<typeof stockContract.warehouseMovementRow>;

export interface WarehouseDetailPageProps {
  warehouseId: string;
}

function formatRupees(paiseStr: string): string {
  const rupees = Number(BigInt(paiseStr)) / 100;
  return `₹${rupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

function typeLabel(t: "physical" | "virtual" | "job_work"): string {
  switch (t) {
    case "physical":
      return "Physical";
    case "virtual":
      return "Virtual";
    case "job_work":
      return "Job Work";
  }
}

const PAGE_SIZE = 50;

export function WarehouseDetailPage({ warehouseId }: WarehouseDetailPageProps) {
  const canManage = usePermission("inventory.manage");
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const utils = trpc.useUtils();
  const query = trpc.warehouses.get.useQuery({ id: warehouseId });
  // Activate / deactivate reuses the warehouse patch mutation — the domain
  // toggles `is_active`, audits the change, and blocks deactivation while the
  // location still holds stock (`WarehouseHasStockError`).
  const toggleActive = trpc.warehouses.update.useMutation();

  const cursor = cursorStack[cursorStack.length - 1];
  const movements = trpc.stock.warehouseMovements.useQuery(
    {
      warehouseId,
      limit: PAGE_SIZE,
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );
  const movRows = (movements.data?.items as MovementRow[] | undefined) ?? [];
  const pageNumber = cursorStack.length + 1;
  const showingFrom = movRows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + movRows.length;

  if (query.isLoading) {
    return (
      <div className="warehouse-detail-page">
        <p className="text-sm text-muted p-6">Loading…</p>
      </div>
    );
  }
  if (query.error) {
    return (
      <div className="warehouse-detail-page">
        <p className="text-sm text-bad p-6">Couldn't load warehouse: {query.error.message}</p>
      </div>
    );
  }
  if (!query.data) return null;
  const w = query.data;

  return (
    <div className="warehouse-detail-page">
      {/* ── Scrollable body ─────────────────────────────────────────── */}
      <main className="wh-main">
        {/* ── Cover card — mirrors customer-detail cover pattern ───── */}
        <section
          className="wh-cover"
          style={{ "--wh-accent": avatarCssColorFor(w.name) } as React.CSSProperties}
        >
          <div className="wh-cover-top">
            <Link to="/inventory/warehouses" className="wh-cover-back" aria-label="Back to warehouses">
              <ArrowLeft size={16} strokeWidth={2.2} aria-hidden />
            </Link>

            <span
              aria-hidden
              className="wh-cover-avatar"
              style={{ background: avatarCssColorFor(w.name) }}
            >
              {initialsFor(w.name)}
            </span>

            <div className="wh-cover-body">
              <div className="wh-cover-name-row">
                <h1 className="wh-cover-name">{w.name}</h1>
                {w.isDefault && <span className="wh-default-chip">Default</span>}
              </div>
              <p className="wh-cover-tagline">
                {[
                  w.code ?? null,
                  typeLabel(w.warehouseType),
                  `Created ${formatDate(w.createdAt)}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              <div className="wh-cover-pills">
                <span className={`wh-type-pill is-${w.warehouseType}`}>
                  {typeLabel(w.warehouseType)}
                </span>
                <span className={`wh-status-pill ${w.isActive ? "is-active" : "is-inactive"}`}>
                  <span className="wh-status-dot" aria-hidden />
                  {w.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {canManage && (
              <div className="wh-cover-actions">
                <button
                  type="button"
                  onClick={() => setStatusConfirmOpen(true)}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg"
                >
                  {w.isActive ? (
                    <>
                      <PowerOff size={12} aria-hidden />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <Power size={12} aria-hidden />
                      Activate
                    </>
                  )}
                </button>
                <Link
                  to="/inventory/warehouses/$warehouseId/edit"
                  params={{ warehouseId }}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg"
                >
                  <Edit3 size={12} aria-hidden />
                  Edit
                </Link>
              </div>
            )}
          </div>

          {/* 4-stat grid */}
          <div className="wh-cover-grid">
            <div className="wh-stat-cell">
              <span className="wh-stat-k">Items stocked</span>
              <span className="wh-stat-v">{w.itemCount}</span>
              <span className="wh-stat-sub">distinct items</span>
            </div>
            <div className="wh-stat-cell">
              <span className="wh-stat-k">Stock value</span>
              <span className="wh-stat-v">{formatRupees(w.totalValuePaise)}</span>
              <span className="wh-stat-sub">at cost (WAC)</span>
            </div>
            <div className="wh-stat-cell">
              <span className="wh-stat-k">Type</span>
              <span className="wh-stat-v wh-stat-v-sm">{typeLabel(w.warehouseType)}</span>
              <span className="wh-stat-sub">
                {w.warehouseType === "physical"
                  ? "physical godown"
                  : w.warehouseType === "virtual"
                    ? "logical partition"
                    : "job work site"}
              </span>
            </div>
            <div className="wh-stat-cell">
              <span className="wh-stat-k">Code</span>
              <span className={`wh-stat-v wh-stat-v-sm ${!w.code ? "wh-stat-na" : ""}`}>
                {w.code ?? "—"}
              </span>
              <span className="wh-stat-sub">short identifier</span>
            </div>
          </div>
        </section>
        {/* Description strip — only shown if warehouse has one */}
        {w.description && (
          <div className="wh-desc-strip">
            <span className="wh-desc-label">Description</span>
            <span className="wh-desc-text">{w.description}</span>
          </div>
        )}

        {/* Stock movements */}
        <div className="panel">
          <div className="panel-head">
            <h3>Stock movements</h3>
            <span className="meta">newest first</span>
          </div>
          {movements.isLoading ? (
            <div className="panel-body">
              <p className="text-sm text-muted">Loading movements…</p>
            </div>
          ) : movRows.length === 0 ? (
            <div className="panel-body">
              <div className="empty-stock">
                <Warehouse size={28} className="empty-icon" aria-hidden />
                <p className="empty-title">No movements yet</p>
                <p className="empty-sub">
                  Goods receipts, adjustments, and transfers will appear here.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] table-fixed border-collapse text-sm">
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-line bg-bg">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">Date</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">Item</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">Type</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted">Qty</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted">Amount</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted">Bal. after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movRows.map((row) => (
                      <MovementTableRow key={row.id} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPagination
                pageNumber={pageNumber}
                showingFrom={showingFrom}
                showingTo={showingTo}
                total={showingTo + (movements.data?.nextCursor ? 1 : 0)}
                sortLabel="newest first"
                hasPrev={cursorStack.length > 0}
                hasNext={!!movements.data?.nextCursor}
                onPrev={() => setCursorStack((s) => s.slice(0, -1))}
                onNext={() => {
                  const next = movements.data?.nextCursor;
                  if (next) setCursorStack((s) => [...s, next]);
                }}
              />
            </>
          )}
        </div>
      </main>

      <ConfirmDialog
        open={statusConfirmOpen}
        onOpenChange={setStatusConfirmOpen}
        title={w.isActive ? "Deactivate this warehouse?" : "Reactivate this warehouse?"}
        description={
          w.isActive
            ? "Inactive warehouses are hidden from item and stock pickers across the app. You can reactivate it anytime. Deactivation is blocked while the warehouse still holds stock."
            : "It becomes selectable again in pickers, transfers, and reports."
        }
        confirmLabel={w.isActive ? "Deactivate" : "Activate"}
        intent={w.isActive ? "danger" : "default"}
        pending={toggleActive.isPending}
        onConfirm={async () => {
          try {
            await toggleActive.mutateAsync({ id: warehouseId, isActive: !w.isActive });
          } catch (err) {
            toast.error("Couldn't update warehouse", {
              description: err instanceof Error ? err.message : "Try again in a moment.",
            });
            throw err; // keep the dialog open with the failure surfaced
          }
          await Promise.all([
            utils.warehouses.get.invalidate({ id: warehouseId }),
            utils.warehouses.list.invalidate(),
          ]);
          toast.success(w.isActive ? "Warehouse deactivated" : "Warehouse activated");
          setStatusConfirmOpen(false);
        }}
      />
    </div>
  );
}

function MovementTableRow({ row }: { row: MovementRow }) {
  const isIn = row.direction === "in";
  const amountRupees = Number(BigInt(row.amountPaise)) / 100;

  return (
    <tr className="border-t border-line hover:bg-raised transition-colors">
      <td className="px-4 py-3 text-xs text-muted whitespace-nowrap">
        {formatDate(row.createdAt)}
      </td>

      <td className="px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden
            className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClassFor(row.itemName)}`}
          >
            {initialsFor(row.itemName)}
          </span>
          <div className="min-w-0">
            <span className="block truncate text-sm font-medium text-ink">{row.itemName}</span>
            {row.notes ? (
              <span className="block truncate text-xs text-muted">{row.notes}</span>
            ) : null}
          </div>
        </div>
      </td>

      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${isIn ? "text-emerald-700" : "text-rose-600"}`}>
          {isIn
            ? <ArrowDown size={11} aria-hidden className="shrink-0" />
            : <ArrowUp size={11} aria-hidden className="shrink-0" />}
          {movementLabel(row.movementType)}
        </span>
      </td>

      <td className="px-4 py-3 text-right">
        <span className={`font-mono font-semibold text-sm tabular-nums ${isIn ? "text-emerald-700" : "text-rose-600"}`}>
          {isIn ? "+" : "−"}{row.quantity} {row.itemUnit}
        </span>
      </td>

      <td className="px-4 py-3 text-right">
        <span className="font-mono text-sm text-ink tabular-nums">
          ₹{amountRupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </td>

      <td className="px-4 py-3 text-right">
        <span className="font-mono font-bold text-sm text-ink tabular-nums">
          {row.qtyAfter} {row.itemUnit}
        </span>
      </td>
    </tr>
  );
}

function movementLabel(type: string): string {
  switch (type) {
    case "opening_balance": return "Opening balance";
    case "grn": return "Goods receipt";
    case "sales_dispatch": return "Sales dispatch";
    case "purchase_return": return "Purchase return";
    case "sales_return": return "Sales return";
    case "transfer_in": return "Transfer in";
    case "transfer_out": return "Transfer out";
    case "job_work_out": return "Job work out";
    case "job_work_in": return "Job work in";
    case "adjustment_in": return "Adjustment in";
    case "adjustment_out": return "Adjustment out";
    case "production_issue": return "Production issue";
    default: return type;
  }
}
